import React, { useState, useEffect, useRef } from 'react';
import {
  Modal,
  TextField,
  Button,
  CircularProgress,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import { Close } from '@mui/icons-material';

import { getAuthToken } from '../../utils/cookieUtils';
import { createSafetyCheck } from '../../api/safetyCheck';
// GraphQL API URL
const GRAPHQL_API_URL =
  process.env.REACT_APP_GRAPHQL_API || 'http://localhost:4000/api/graphql';

export type AddTrailerStatus = 'EMPTY' | 'LOADED';

export type AddTrailerLocationType = 'dock' | 'yard' | 'facility_zone';

/** Where the trailer goes: a dock, a yard spot, or a facility zone. */
export interface AddTrailerLocation {
  type: AddTrailerLocationType;
  /** The dock's, yard spot's or facility zone's uuid. */
  uuid: string;
}

export interface AddTrailerValues {
  scac: string;
  trailerNumber: string;
  status: AddTrailerStatus;
  /** Null when left empty. */
  notes: string | null;
  location: AddTrailerLocation;
}

// A dock or a yard spot: the kinds of place the picker lists one by one.
type PlaceType = Exclude<AddTrailerLocationType, 'facility_zone'>;

const PLACE_KIND_NAMES: Record<PlaceType, string> = { dock: 'dock', yard: 'yard spot' };

// A dock or yard spot from the lists, by uuid.
function findPlace(type: PlaceType, uuid: string, docks: any[], spots: any[]): any {
  switch (type) {
    case 'dock':
      return docks.find((door) => door.dockUuid === uuid);
    case 'yard':
      return spots.find((spot) => spot.yard_spot_uuid === uuid);
  }
}

// A dock's or yard spot's name, as the picker shows it.
function placeLabel(type: PlaceType, place: any): string {
  switch (type) {
    case 'dock':
      return place.dockName || place.number || '';
    case 'yard':
      return place.yard_spot_name || '';
  }
}

// Why a dock or yard spot is not offered, as the end of a sentence about it,
// or null when it is free.
function unavailableReason(place: any): string | null {
  if (place.trailer_name) return `is occupied by trailer ${place.trailer_name}`;
  if (place.isLocked) return 'is locked by a move in progress';
  if (place.configuration?.status === 'unavailable') return 'is marked unavailable';
  return null;
}

// Props interface
interface AddTrailerModalProps {
  open: boolean;
  onClose: () => void;
  /** Called with the `addTrailer` result. Not called when `onSubmit` is given. */
  onSuccess?: (trailerData: any) => void;
  onError?: (error: string) => void;
  facilityId: string;
  /** Prefills the trailer number each time the modal opens (e.g. from an OCR read). */
  initialTrailerNumber?: string;
  /** Prefills the SCAC each time the modal opens. */
  initialScac?: string;
  /**
   * Preselects this location each time the modal opens, once the location lists
   * have it (e.g. the spot an RTLS read saw the trailer in). The person can
   * still pick another. A dock or spot that is occupied, locked or unavailable
   * is not offered, so it is left unselected and the modal says why.
   */
  initialLocation?: AddTrailerLocation | null;
  /**
   * Replaces the built-in `addTrailer` mutation (and the safety check that
   * follows it) with the caller's own handling of the values. Throw to show the
   * message in the modal and keep it open; the caller closes it on success.
   */
  onSubmit?: (values: AddTrailerValues) => Promise<void>;
  /** Shows a spinner in place of the location picker while the lists load. */
  loadingLocations?: boolean;
  availableDockDoors?: any[];
  availableYardSpots?: any[];
  availableFacilityZones?: any[];
  zones?: any[];
  onSafetyCheckCreated?: (taskData: { uuid: string }) => void;
}

const AddTrailerModal: React.FC<AddTrailerModalProps> = ({
  open,
  onClose,
  onSuccess = () => {},
  onError = () => {},
  facilityId,
  initialTrailerNumber,
  initialScac,
  initialLocation,
  onSubmit,
  loadingLocations = false,
  availableDockDoors = [],
  availableYardSpots = [],
  availableFacilityZones = [],
  zones = [],
  onSafetyCheckCreated
}) => {
  // Form state
  const [scac, setScac] = useState<string>('');
  const [trailerNumber, setTrailerNumber] = useState<string>('');
  const [status, setStatus] = useState<AddTrailerStatus>('EMPTY');
  const [notes, setNotes] = useState<string>('');
  const [selectedDestination, setSelectedDestination] = useState<string>('');
  const [selectedDestinationId, setSelectedDestinationId] = useState<string>('');
  const [selectedDestinationType, setSelectedDestinationType] = useState<
    'dock' | 'yard' | 'facility_zone'
  >('dock');
  const [selectedFacilityZone, setSelectedFacilityZone] = useState<string>('');
  const [dockFilter, setDockFilter] = useState<string>('');
  const [yardFilter, setYardFilter] = useState<string>('');
  const [selectedZoneFilter, setSelectedZoneFilter] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [showCreateAnywayButton, setShowCreateAnywayButton] = useState<boolean>(false);
  const [forceCreate, setForceCreate] = useState<boolean>(false);
  const [addSafetyCheck, setAddSafetyCheck] = useState<boolean>(false);
  // Whether `initialLocation` has been applied since the modal last opened.
  const initialLocationApplied = useRef<boolean>(false);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (open) {
      setScac(initialScac ?? '');
      setTrailerNumber(initialTrailerNumber ?? '');
      setStatus('EMPTY');
      setNotes('');
      setSelectedDestination('');
      setSelectedDestinationId('');
      setSelectedFacilityZone('');
      setDockFilter('');
      setYardFilter('');
      setSelectedZoneFilter(null);
      setSelectedDestinationType('dock');
      setError(null);
      setShowCreateAnywayButton(false);
      setForceCreate(false);
      setAddSafetyCheck(false);
      initialLocationApplied.current = false;
    }
    // Only depend on 'open' to avoid resetting when location data loads
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Preselect `initialLocation` once the lists have it. They can load after the
  // modal opens, so this waits for them, and it applies once per opening so it
  // never undoes a choice the person has made since.
  useEffect(() => {
    if (!open || !initialLocation || initialLocationApplied.current) return;
    const { type, uuid } = initialLocation;

    if (type === 'facility_zone') {
      const zoneListed = availableFacilityZones.some(
        (zone) => (zone.facility_zone_uuid || zone.id) === uuid
      );
      if (!zoneListed) return;

      initialLocationApplied.current = true;
      setSelectedDestinationType('facility_zone');
      setSelectedFacilityZone(uuid);
      return;
    }

    const place = findPlace(type, uuid, availableDockDoors, availableYardSpots);
    if (!place) return;

    initialLocationApplied.current = true;
    setSelectedDestinationType(type);

    const label = placeLabel(type, place);
    const unavailableBecause = unavailableReason(place);
    if (unavailableBecause) {
      // Left unselected and unfiltered, so the person can pick another.
      setError(
        `Suggested ${PLACE_KIND_NAMES[type]} ${label} ${unavailableBecause}. Choose another location.`
      );
      return;
    }

    if (type === 'dock') {
      setDockFilter(label);
    } else {
      setYardFilter(label);
      // The spot's own zone, so the zone picker shows it too (and a spot name
      // shared across zones narrows to this one).
      setSelectedZoneFilter(place.facility_zone_uuid || null);
    }
    setSelectedDestination(label);
    setSelectedDestinationId(uuid);
  }, [
    open,
    initialLocation,
    availableDockDoors,
    availableYardSpots,
    availableFacilityZones
  ]);

  // Debug: Log available locations when they change (separate effect)
  useEffect(() => {
    if (open) {
      console.log('AddTrailerModal location data updated:', {
        availableDockDoors: availableDockDoors.length,
        availableYardSpots: availableYardSpots.length,
        availableFacilityZones: availableFacilityZones.length
      });
    }
  }, [open, availableDockDoors, availableYardSpots, availableFacilityZones]);

  // Sorting function for dock doors and yard spots
  const sortLocationsByOrder = (locations: any[], nameField: string) => {
    return locations.sort((a, b) => {
      // Get order values from configuration
      const orderA = a.configuration?.order;
      const orderB = b.configuration?.order;

      // Check if order values are valid (not null, undefined, or 0)
      const hasValidOrderA = orderA !== null && orderA !== undefined && orderA !== 0;
      const hasValidOrderB = orderB !== null && orderB !== undefined && orderB !== 0;

      // If both have valid order, use order for sorting
      if (hasValidOrderA && hasValidOrderB) {
        return orderA - orderB;
      }

      // If one has valid order and the other doesn't, prioritize the one with order
      if (hasValidOrderA && !hasValidOrderB) {
        return -1;
      }
      if (hasValidOrderB && !hasValidOrderA) {
        return 1;
      }

      // Both don't have valid order - fall back to name sorting with intelligent numeric handling
      const nameA = a[nameField] || '';
      const nameB = b[nameField] || '';

      // Extract numeric parts from names like "D35" → 35, "A15" → 15
      const numA = parseInt(nameA.replace(/[^0-9]/g, ''), 10);
      const numB = parseInt(nameB.replace(/[^0-9]/g, ''), 10);

      if (!isNaN(numA) && !isNaN(numB)) {
        // Both have numeric parts, compare numerically (D1, D2, D10 instead of D1, D10, D2)
        return numA - numB;
      }

      // At least one doesn't have numeric parts, use intelligent string comparison
      return nameA.localeCompare(nameB, undefined, {
        numeric: true,
        sensitivity: 'base'
      });
    });
  };

  // Get available destinations based on destination type
  const getAvailableDestinations = () => {
    if (selectedDestinationType === 'dock') {
      const filtered = availableDockDoors
        .filter((door) => {
          const hasValidName =
            (door.dockName || door.number) && (door.dockName || door.number).trim();
          const isAvailable =
            !door.trailer_name &&
            door.configuration?.status !== 'unavailable' &&
            !door.isLocked;
          const matchesFilter =
            !dockFilter.trim() ||
            (hasValidName &&
              (door.dockName || door.number)
                .toLowerCase()
                .includes(dockFilter.toLowerCase()));
          return hasValidName && isAvailable && matchesFilter;
        })
        .map((door) => ({
          id: door.dockUuid,
          label: door.dockName || door.number || door.id,
          zoneName: door.zoneName,
          ...door
        }));

      // Sort by order/name
      return sortLocationsByOrder(filtered, 'label');
    } else if (selectedDestinationType === 'yard') {
      const filtered = availableYardSpots
        .filter((spot) => {
          const hasValidName = spot.yard_spot_name && spot.yard_spot_name.trim();
          const isAvailable =
            !spot.trailer_name &&
            spot.configuration?.status !== 'unavailable' &&
            !spot.isLocked;
          const matchesZone =
            !selectedZoneFilter || spot.facility_zone_uuid === selectedZoneFilter;
          const matchesFilter =
            !yardFilter.trim() ||
            (hasValidName &&
              spot.yard_spot_name.toLowerCase().includes(yardFilter.toLowerCase()));
          return hasValidName && isAvailable && matchesZone && matchesFilter;
        })
        .map((spot) => ({
          id: spot.yard_spot_uuid,
          label: spot.yard_spot_name,
          zoneName: spot.zoneName,
          ...spot
        }));

      // Sort by order/name
      return sortLocationsByOrder(filtered, 'label');
    }
    return [];
  };

  // Handle destination type change
  const handleDestinationTypeChange = (type: 'dock' | 'yard' | 'facility_zone') => {
    setSelectedDestinationType(type);
    setSelectedDestination('');
    setSelectedDestinationId('');
    setSelectedFacilityZone('');
    setDockFilter('');
    setYardFilter('');
    setSelectedZoneFilter(null);
    setError(null);
  };

  // Handle trailer creation
  const handleCreateTrailer = async (forceCreateOverride?: boolean) => {
    const shouldForceCreate =
      forceCreateOverride !== undefined ? forceCreateOverride : forceCreate;
    if (!scac.trim()) {
      setError('Please enter SCAC');
      return;
    }

    if (!trailerNumber.trim()) {
      setError('Please enter trailer number');
      return;
    }

    if (selectedDestinationType === 'facility_zone' && !selectedFacilityZone.trim()) {
      setError('Please select a facility zone');
      return;
    }

    if (selectedDestinationType !== 'facility_zone' && !selectedDestination.trim()) {
      setError('Please select a destination');
      return;
    }

    if (onSubmit) {
      try {
        setIsCreating(true);
        setError(null);
        await onSubmit({
          scac: scac.trim(),
          trailerNumber: trailerNumber.trim(),
          status,
          notes: notes.trim() || null,
          location: {
            type: selectedDestinationType,
            uuid:
              selectedDestinationType === 'facility_zone'
                ? selectedFacilityZone
                : selectedDestinationId
          }
        });
      } catch (error: any) {
        const errorMessage = error.message || 'Failed to add trailer';
        setError(errorMessage);
        onError(errorMessage);
      } finally {
        setIsCreating(false);
      }
      return;
    }

    try {
      setIsCreating(true);
      setError(null);

      // Prepare mutation variables
      const variables: any = {
        scac: scac.trim(),
        trailerNumber: trailerNumber.trim(),
        facilityId: facilityId,
        status,
        notes: notes.trim() || null,
        force_create_trailer_if_active: shouldForceCreate
      };

      // Add location-specific fields based on destination type
      if (selectedDestinationType === 'dock') {
        const selectedDock = availableDockDoors.find(
          (d) => d.dockUuid === selectedDestinationId
        );
        if (selectedDock) {
          variables.dockUuid = selectedDock.dockUuid;
          variables.warehouseZoneUuid = selectedDock.zoneUuid;
        }
      } else if (selectedDestinationType === 'yard') {
        const selectedSpot = availableYardSpots.find(
          (s) => s.yard_spot_uuid === selectedDestinationId
        );
        if (selectedSpot) {
          variables.yardSpotUuid = selectedSpot.yard_spot_uuid;
          variables.facilityZoneUuid = selectedSpot.facility_zone_uuid;
        }
      } else if (selectedDestinationType === 'facility_zone') {
        variables.facilityZoneUuid = selectedFacilityZone;
      }

      // GraphQL mutation
      const mutation = `
        mutation AddTrailer(
          $scac: String!,
          $trailerNumber: String!,
          $facilityId: ID!,
          $status: String!,
          $notes: String,
          $dockUuid: String,
          $facilityZoneUuid: String,
          $warehouseZoneUuid: String,
          $yardSpotUuid: String,
          $force_create_trailer_if_active: Boolean
        ) {
          addTrailer(input: {
            scac: $scac
            trailerNumber: $trailerNumber
            facilityId: $facilityId
            status: $status
            notes: $notes
            dockUuid: $dockUuid
            facilityZoneUuid: $facilityZoneUuid
            warehouseZoneUuid: $warehouseZoneUuid
            yardSpotUuid: $yardSpotUuid
            force_create_trailer_if_active: $force_create_trailer_if_active
          }) {
            success
            trailer {
              uuid
              trailerNumber
              carrier {
                id
                name
              }
              status {
                name
              }
            }
            lineage {
              uuid
              isActive
              notes
              currentFacilityZoneUuid
              currentWarehouseZoneUuid
              currentYardSpotUuid
            }
            state {
              uuid
              depth
              facilityZoneUuid
              warehouseZoneUuid
              yardSpotUuid
            }
            errors
          }
        }
      `;

      // Get auth token from cookie or localStorage
      const authToken = getAuthToken();

      const response = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          query: mutation,
          variables
        })
      });

      const result = await response.json();

      if (result.errors) {
        throw new Error(result.errors[0]?.message || 'Failed to add trailer');
      }

      const trailerData = result.data?.addTrailer;
      if (!trailerData) {
        throw new Error('No trailer data returned from server');
      }

      if (!trailerData.success) {
        throw new Error(trailerData.errors?.join(', ') || 'Failed to add trailer');
      }

      // Call success callback
      onSuccess(trailerData);

      // Create safety check if checkbox was checked
      if (addSafetyCheck && trailerData.trailer?.uuid) {
        try {
          const destinationLabel = selectedDestination;
          const description =
            selectedDestinationType === 'dock'
              ? `Door ${destinationLabel} Safety Check`
              : `Spot ${destinationLabel} Safety Check`;

          const safetyCheckResult = await createSafetyCheck({
            trailer_uuid: trailerData.trailer.uuid,
            facility_id: facilityId,
            dock_uuid: selectedDestinationType === 'dock' ? selectedDestinationId : null,
            yard_spot_uuid:
              selectedDestinationType === 'yard' ? selectedDestinationId : null,
            description
          });
          onSafetyCheckCreated?.(safetyCheckResult);
        } catch (safetyError: any) {
          console.warn('Safety check creation failed:', safetyError);
          onError(
            `Trailer created successfully, but safety check failed: ${safetyError.message}`
          );
        }
      }
    } catch (error: any) {
      console.error('Failed to add trailer:', error);
      const errorMessage = error.message || 'Failed to add trailer';
      setError(errorMessage);

      // Check if error contains "is already active"
      if (errorMessage.toLowerCase().includes('is already active')) {
        setShowCreateAnywayButton(true);
      }

      onError(errorMessage);
    } finally {
      setIsCreating(false);
    }
  };

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const handleCreateAnyway = () => {
    setShowCreateAnywayButton(false);
    // Trigger the creation again with force flag
    handleCreateTrailer(true);
  };

  const availableDestinations = getAvailableDestinations();

  return (
    <Modal open={open} onClose={handleClose} className="flex justify-center items-center">
      <div className="w-[450px] bg-white border p-5 max-h-[90vh] overflow-y-auto relative">
        <Close
          className="absolute top-4 right-4 cursor-pointer hover:text-slate-400"
          onClick={handleClose}
        />
        <h1 className="text-xl font-bold mb-4">Add New Trailer</h1>

        {/* SCAC Field */}
        <div className="mb-4">
          <TextField
            label="SCAC *"
            variant="outlined"
            fullWidth
            required
            value={scac}
            onChange={(e) => {
              // Convert to uppercase and allow only alphanumeric characters
              const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
              setScac(value);
            }}
            helperText="Enter the carrier's SCAC code (e.g. ABCD)"
            disabled={isCreating}
            error={!scac.trim() && error?.includes('SCAC')}
          />
        </div>

        {/* Trailer Number Field */}
        <div className="mb-4">
          <TextField
            label="Trailer Number *"
            variant="outlined"
            fullWidth
            required
            value={trailerNumber}
            onChange={(e) => {
              // Convert to uppercase and allow only alphanumeric characters
              const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
              setTrailerNumber(value);
            }}
            helperText="Enter the trailer number - alphanumeric only (e.g. TRL12345)"
            disabled={isCreating}
            error={!trailerNumber.trim() && error?.includes('trailer number')}
          />
        </div>

        {/* Status Field */}
        <div className="mb-4">
          <FormControl fullWidth variant="outlined">
            <InputLabel>Status *</InputLabel>
            <Select
              value={status}
              onChange={(e) => setStatus(e.target.value as AddTrailerStatus)}
              label="Status *"
              disabled={isCreating}
            >
              <MenuItem value="EMPTY">EMPTY</MenuItem>
              <MenuItem value="LOADED">LOADED</MenuItem>
            </Select>
          </FormControl>
        </div>

        {/* Notes Field */}
        <div className="mb-4">
          <TextField
            label="Notes (Optional)"
            variant="outlined"
            fullWidth
            multiline
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            helperText="Add any notes about the trailer"
            disabled={isCreating}
            placeholder="Enter any additional notes..."
          />
        </div>

        {/* Show error if any */}
        {error && (
          <div className="mb-4">
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">
              {error}
            </div>
            {showCreateAnywayButton && (
              <Button
                variant="outlined"
                color="warning"
                onClick={handleCreateAnyway}
                disabled={isCreating}
                className="mt-2"
                fullWidth
              >
                Create Anyway
              </Button>
            )}
          </div>
        )}

        {/* Location Type Selection */}
        <div className="mb-4">
          <h2 className="font-semibold mb-2">Location Type</h2>
          <div className="flex gap-2 flex-wrap">
            <button
              className={`px-3 py-2 rounded text-sm ${
                selectedDestinationType === 'dock'
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-200 hover:bg-gray-300'
              }`}
              onClick={() => handleDestinationTypeChange('dock')}
            >
              Dock Door
            </button>
            <button
              className={`px-3 py-2 rounded text-sm ${
                selectedDestinationType === 'yard'
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-200 hover:bg-gray-300'
              }`}
              onClick={() => handleDestinationTypeChange('yard')}
            >
              Yard Spot
            </button>
            <button
              className={`px-3 py-2 rounded text-sm ${
                selectedDestinationType === 'facility_zone'
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-200 hover:bg-gray-300'
              }`}
              onClick={() => handleDestinationTypeChange('facility_zone')}
            >
              Facility Zone
            </button>
          </div>
        </div>

        {/* Location Selection */}
        <div className="mb-4">
          {loadingLocations ? (
            <div className="flex items-center gap-2 py-3 text-sm text-gray-600">
              <CircularProgress size={16} />
              Loading locations...
            </div>
          ) : selectedDestinationType === 'facility_zone' ? (
            /* Facility zone selection */
            <div>
              <h2 className="font-semibold mb-2">Select Facility Zone</h2>

              <div className="space-y-3">
                {/* Quick selection from available facility zones */}
                <div>
                  <div className="text-sm text-gray-600 mb-1">
                    Select from available facility zones:
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {(() => {
                      // Get available facility zones
                      const availableFacilityZonesArray = availableFacilityZones || [];

                      if (availableFacilityZonesArray.length === 0) {
                        return (
                          <div className="text-amber-600 p-2 border border-amber-200 rounded text-sm bg-amber-50 w-full">
                            No facility zones found.
                          </div>
                        );
                      }

                      return availableFacilityZonesArray.map((zone: any) => {
                        return (
                          <button
                            key={zone.facility_zone_uuid || zone.id}
                            className={`px-3 py-2 border rounded text-sm ${
                              selectedFacilityZone ===
                              (zone.facility_zone_uuid || zone.id)
                                ? 'bg-blue-100 border-blue-500'
                                : 'bg-gray-50 hover:bg-gray-100'
                            }`}
                            onClick={() =>
                              setSelectedFacilityZone(zone.facility_zone_uuid || zone.id)
                            }
                          >
                            <div className="flex flex-col">
                              <span className="font-medium">
                                {zone.facility_zone_name || zone.name}
                              </span>
                              {zone.zone_type && (
                                <span className="text-xs text-gray-500">
                                  {zone.zone_type}
                                </span>
                              )}
                            </div>
                          </button>
                        );
                      });
                    })()}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* Dock Door or Yard Spot selection */
            <div>
              <h2 className="font-semibold mb-2">
                Select {selectedDestinationType === 'dock' ? 'Dock Door' : 'Yard Spot'}
              </h2>

              {/* Zone filter dropdown for yard spots */}
              {selectedDestinationType === 'yard' && zones.length > 0 && (
                <div className="mb-4">
                  <h3 className="font-semibold mb-2">Filter by Zone (Optional)</h3>
                  <Autocomplete
                    options={zones.map((zone) => ({
                      label: zone.name,
                      value: zone.id || zone.facility_zone_uuid,
                      color: zone.color
                    }))}
                    value={
                      selectedZoneFilter
                        ? zones.find(
                            (z) => (z.id || z.facility_zone_uuid) === selectedZoneFilter
                          )
                          ? {
                              label: zones.find(
                                (z) =>
                                  (z.id || z.facility_zone_uuid) === selectedZoneFilter
                              )!.name,
                              value: selectedZoneFilter,
                              color: zones.find(
                                (z) =>
                                  (z.id || z.facility_zone_uuid) === selectedZoneFilter
                              )!.color
                            }
                          : null
                        : null
                    }
                    onChange={(_, newValue) => {
                      setSelectedZoneFilter(newValue?.value || null);
                      // Clear selected destination and yard filter when zone filter changes
                      setSelectedDestination('');
                      setSelectedDestinationId('');
                      setYardFilter('');
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Zone"
                        variant="outlined"
                        fullWidth
                        helperText="Filter yard spots by zone"
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props}>
                        <div className="flex items-center">
                          <div
                            className="w-3 h-3 rounded mr-2"
                            style={{ backgroundColor: option.color }}
                          />
                          {option.label}
                        </div>
                      </li>
                    )}
                  />
                </div>
              )}

              <TextField
                label={`Filter ${selectedDestinationType === 'dock' ? 'Dock Doors' : 'Yard Spots'}`}
                variant="outlined"
                fullWidth
                value={selectedDestinationType === 'dock' ? dockFilter : yardFilter}
                onChange={(e) => {
                  const filterValue = e.target.value;

                  if (selectedDestinationType === 'dock') {
                    setDockFilter(filterValue);

                    // Get available dock doors with valid names/numbers
                    const availableDoors = availableDockDoors.filter(
                      (door) =>
                        !door.trailer_name && // Not occupied
                        door.configuration?.status !== 'unavailable' && // Not unavailable
                        !door.isLocked && // Not locked
                        (door.dockName || door.number) && // Has valid name/number
                        (door.dockName || door.number).trim() // Not empty
                    );

                    // Auto-selection logic
                    if (filterValue.trim()) {
                      // Find doors that match the filter
                      const matchingDoors = availableDoors.filter((door) => {
                        const doorName = door.dockName || door.number || '';
                        return doorName.toLowerCase().includes(filterValue.toLowerCase());
                      });

                      // Check for exact match
                      const exactMatch = matchingDoors.find((door) => {
                        const doorName = door.dockName || door.number || '';
                        return doorName.toLowerCase() === filterValue.toLowerCase();
                      });

                      // Auto-select if there's exactly 1 result and it's an exact match
                      if (matchingDoors.length === 1 && exactMatch) {
                        setSelectedDestination(
                          exactMatch.dockName || exactMatch.number || ''
                        );
                        setSelectedDestinationId(exactMatch.dockUuid);
                      } else {
                        // Clear selection if multiple results or no exact match
                        setSelectedDestination('');
                        setSelectedDestinationId('');
                      }
                    } else {
                      // Clear selection when filter is empty
                      setSelectedDestination('');
                      setSelectedDestinationId('');
                    }
                  } else {
                    setYardFilter(filterValue);

                    // Get available yard spots with valid names/numbers
                    const availableSpots = availableYardSpots.filter(
                      (spot) =>
                        !spot.trailer_name && // Not occupied
                        spot.configuration?.status !== 'unavailable' && // Not unavailable
                        !spot.isLocked && // Not locked
                        spot.yard_spot_name && // Has valid name
                        spot.yard_spot_name.trim() && // Not empty
                        (!selectedZoneFilter ||
                          spot.facility_zone_uuid === selectedZoneFilter) // Matches zone filter
                    );

                    // Auto-selection logic
                    if (filterValue.trim()) {
                      // Find spots that match the filter
                      const matchingSpots = availableSpots.filter((spot) => {
                        const spotName = spot.yard_spot_name || '';
                        return spotName.toLowerCase().includes(filterValue.toLowerCase());
                      });

                      // Check for exact match
                      const exactMatch = matchingSpots.find((spot) => {
                        const spotName = spot.yard_spot_name || '';
                        return spotName.toLowerCase() === filterValue.toLowerCase();
                      });

                      // Auto-select if there's exactly 1 result and it's an exact match
                      if (matchingSpots.length === 1 && exactMatch) {
                        setSelectedDestination(exactMatch.yard_spot_name || '');
                        setSelectedDestinationId(exactMatch.yard_spot_uuid);
                      } else {
                        // Clear selection if multiple results or no exact match
                        setSelectedDestination('');
                        setSelectedDestinationId('');
                      }
                    } else {
                      // Clear selection when filter is empty
                      setSelectedDestination('');
                      setSelectedDestinationId('');
                    }
                  }
                }}
                helperText={`Type to filter ${selectedDestinationType === 'dock' ? 'dock doors' : 'yard spots'} below (e.g. ${selectedDestinationType === 'dock' ? 'D10' : 'A15'})`}
                placeholder={`Type ${selectedDestinationType === 'dock' ? 'dock door' : 'yard spot'} name to filter...`}
              />

              {/* Available destinations */}
              {availableDestinations.length > 0 && (
                <div className="mt-3">
                  <div className="text-sm text-gray-600 mb-1">
                    Available{' '}
                    {selectedDestinationType === 'dock' ? 'Dock Doors' : 'Yard Spots'}:
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {availableDestinations.map((dest) => (
                      <button
                        key={dest.id}
                        className={`px-2 py-1 border rounded text-sm ${
                          selectedDestinationId === dest.id
                            ? 'bg-blue-100 border-blue-500'
                            : 'bg-gray-50 hover:bg-gray-100'
                        }`}
                        onClick={() => {
                          setError(null);
                          setSelectedDestination(dest.label);
                          setSelectedDestinationId(dest.id);
                          // Update the filter to match the selected destination
                          if (selectedDestinationType === 'dock') {
                            setDockFilter(dest.label);
                          } else {
                            setYardFilter(dest.label);
                          }
                        }}
                      >
                        <div className="flex flex-col">
                          <span className="font-medium">{dest.label}</span>
                          {dest.zoneName && (
                            <span className="text-xs text-gray-500">{dest.zoneName}</span>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* No available destinations message */}
              {availableDestinations.length === 0 && (
                <div className="mt-3 text-amber-600 bg-amber-50 p-2 border border-amber-200 rounded text-sm">
                  {selectedDestinationType === 'dock'
                    ? dockFilter.trim()
                      ? `No dock doors found matching "${dockFilter}". Try clearing the filter.`
                      : `No available dock doors found.`
                    : yardFilter.trim() && selectedZoneFilter
                      ? `No yard spots found matching "${yardFilter}" in the selected zone. Try clearing the filters.`
                      : yardFilter.trim()
                        ? `No yard spots found matching "${yardFilter}". Try clearing the filter.`
                        : selectedZoneFilter
                          ? `No available yard spots found in the selected zone. Try clearing the zone filter.`
                          : `No available yard spots found.`}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Add Safety Check checkbox - only for dock/yard placements, and only
            when this modal adds the trailer itself (`onSubmit` replaces that) */}
        {selectedDestinationType !== 'facility_zone' && !onSubmit && (
          <div className="mb-4">
            <FormControlLabel
              control={
                <Checkbox
                  checked={addSafetyCheck}
                  onChange={(e) => setAddSafetyCheck(e.target.checked)}
                  disabled={isCreating}
                />
              }
              label="Add Safety Check"
            />
          </div>
        )}

        <div className="flex justify-between pt-3 border-t">
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleCreateTrailer()}
            disabled={
              !scac.trim() ||
              !trailerNumber.trim() ||
              (selectedDestinationType === 'facility_zone' &&
                !selectedFacilityZone.trim()) ||
              (selectedDestinationType !== 'facility_zone' &&
                !selectedDestination.trim()) ||
              isCreating
            }
          >
            {isCreating ? (
              <>
                <CircularProgress size={16} className="mr-2" />
                Adding...
              </>
            ) : (
              'Add Trailer'
            )}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default AddTrailerModal;
